import type { Annotation } from 'api';
import { useCallback, useEffect, useRef } from 'react';
import { styled, useTheme } from 'ui';

import {
  applyEnhancementFilter,
  drawAnnotationsWithTransform,
  drawImageWithFallback,
  resetFilter,
} from '../../utils/canvas/canvas';

const ZOOM_LEVEL = 1.5;

interface ZoomOverlayProps {
  isVisible: boolean;
  mousePosition: { x: number; y: number };
  sourceCanvas: HTMLCanvasElement | null;
  sourceRect: DOMRect | null;
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories: string[];
  labelColourMap: Map<string, readonly number[]>;
}

const ZoomContainer = styled('div')<{ isVisible: boolean; x: number; y: number; borderColour: string }>(
  ({ borderColour, isVisible, x, y }) => ({
    border: `4px solid ${borderColour}`,
    borderRadius: '50%',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(0, 0, 0, 0.1)',
    height: 250,
    left: x - 125,
    opacity: isVisible ? 1 : 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'fixed',
    top: y - 125,
    transform: isVisible ? 'scale(1)' : 'scale(0.8)',
    transition: 'opacity 0.2s ease-in-out, transform 0.1s ease-out',
    width: 250,
    zIndex: 1000,
  })
);

const ZoomCanvas = styled('canvas')({
  borderRadius: '50%',
  height: '100%',
  width: '100%',
});

export const ZoomOverlay = ({
  isVisible,
  mousePosition,
  sourceCanvas,
  sourceRect,
  imageSrc,
  annotations,
  hiddenCategories,
  labelColourMap,
}: ZoomOverlayProps) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const highResImageRef = useRef<HTMLImageElement | null>(null);

  const drawAnnotationsOnZoom = useCallback(
    (
      context: CanvasRenderingContext2D,
      sourceX: number,
      sourceY: number,
      sourceWidth: number,
      sourceHeight: number
    ) => {
      drawAnnotationsWithTransform(
        context,
        annotations,
        hiddenCategories,
        labelColourMap,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight
      );
    },
    [annotations, hiddenCategories, labelColourMap]
  );

  const fallbackToSourceCanvas = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!sourceCanvas || !sourceRect) return;
      drawImageWithFallback(ctx, sourceCanvas, sourceRect, mousePosition, ZOOM_LEVEL);
    },
    [mousePosition, sourceCanvas, sourceRect]
  );

  const drawZoomedArea = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!highResImageRef.current || !sourceCanvas || !sourceRect) return;

      const canvasX = mousePosition.x - sourceRect.left;
      const canvasY = mousePosition.y - sourceRect.top;
      const zoomRadius = 50 / ZOOM_LEVEL;

      const scaleX = highResImageRef.current.width / sourceRect.width;
      const scaleY = highResImageRef.current.height / sourceRect.height;

      let sourceX = (canvasX - zoomRadius) * scaleX;
      let sourceY = (canvasY - zoomRadius) * scaleY;
      const sourceWidth = zoomRadius * 2 * scaleX;
      const sourceHeight = zoomRadius * 2 * scaleY;

      sourceX = Math.max(0, Math.min(sourceX, highResImageRef.current.width - sourceWidth));
      sourceY = Math.max(0, Math.min(sourceY, highResImageRef.current.height - sourceHeight));

      ctx.clearRect(0, 0, 250, 250);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      try {
        applyEnhancementFilter(ctx);
        ctx.drawImage(highResImageRef.current, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, 250, 250);
        resetFilter(ctx);
        drawAnnotationsOnZoom(ctx, sourceX, sourceY, sourceWidth, sourceHeight);
      } catch {
        resetFilter(ctx);
        fallbackToSourceCanvas(ctx);
      }
    },
    [mousePosition, sourceCanvas, sourceRect, drawAnnotationsOnZoom, fallbackToSourceCanvas]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceCanvas || !sourceRect || !isVisible || !imageSrc) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = 250 * devicePixelRatio;
    canvas.height = 250 * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);

    if (!highResImageRef.current || highResImageRef.current.src !== imageSrc) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = imageSrc;

      img.onload = () => {
        highResImageRef.current = img;
        drawZoomedArea(ctx);
      };

      img.onerror = () => {
        fallbackToSourceCanvas(ctx);
      };
    } else {
      drawZoomedArea(ctx);
    }
  }, [isVisible, mousePosition, sourceCanvas, sourceRect, imageSrc, drawZoomedArea, fallbackToSourceCanvas]);

  if (!isVisible) return null;

  return (
    <ZoomContainer
      isVisible={isVisible}
      x={mousePosition.x}
      y={mousePosition.y}
      borderColour={theme.palette.primary.main}
      data-testid="zoom-overlay"
    >
      <ZoomCanvas ref={canvasRef} />
    </ZoomContainer>
  );
};
